import { useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CheckCircle,
  ChevronDown,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
  Mail,
  Send,
  Settings2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';

import { EMAIL_PROVIDERS, EmailProvider } from '@/constants/email-providers';
import { SANS_BOITE_PROVIDER_ID } from '@/schemas/smtp-config.schema';
import { useAuthStore } from '@/store/auth.store';
import { sendersService as featureSendersService } from '@/features/senders/services/senders.service';
import { sendersService } from '@/services/senders.service';
import { cn } from '@/utils/cn';
import { Button } from '../ui/Button';

interface SmtpConfigFormProps {
  form: UseFormReturn<FieldValues>;
  showTestButton?: boolean;
}

const MANUAL_PROVIDER_IDS = [
  'ovh',
  'ionos',
  'hostinger',
  'godaddy',
  'lws',
  'coaxis',
  'outlook',
  'other',
];

const providerLabels: Record<string, string> = {
  ovh: 'OVHcloud',
  ionos: 'IONOS',
  hostinger: 'Hostinger',
  godaddy: 'GoDaddy',
  lws: 'LWS',
  coaxis: 'Coaxis',
  outlook: 'Outlook SMTP',
  other: 'Autre serveur',
};

function getProviderLabel(provider: EmailProvider) {
  return provider.name || providerLabels[provider.id] || 'Fournisseur';
}

function getHumanSmtpError(message?: string) {
  if (!message) {
    return 'La connexion a échoué. Vérifiez les identifiants et les paramètres du serveur.';
  }

  const normalized = message.toLowerCase();

  if (
    normalized.includes('535') ||
    normalized.includes('auth') ||
    normalized.includes('login') ||
    normalized.includes('password') ||
    normalized.includes('credential')
  ) {
    return "Le serveur refuse l'adresse ou le mot de passe. Pour Microsoft/Gmail, utilisez plutôt la connexion sécurisée.";
  }

  if (
    normalized.includes('timeout') ||
    normalized.includes('timed out') ||
    normalized.includes('econnrefused') ||
    normalized.includes('enotfound') ||
    normalized.includes('getaddrinfo')
  ) {
    return 'Le serveur SMTP ne répond pas. Vérifiez le fournisseur choisi, le serveur et le port.';
  }

  if (
    normalized.includes('ssl') ||
    normalized.includes('tls') ||
    normalized.includes('certificate') ||
    normalized.includes('wrong version')
  ) {
    return 'Le mode de sécurité ne correspond pas au serveur. Essayez TLS sur le port 587 ou SSL sur le port 465.';
  }

  return message;
}

export function SmtpConfigForm({
  form,
  showTestButton = true,
}: SmtpConfigFormProps) {
  const { t } = useTranslation();
  const [isTesting, setIsTesting] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(() => {
    const provider = form.getValues('smtpProvider');
    return Boolean(
      provider &&
        provider !== 'gmail-oauth' &&
        provider !== 'microsoft365-oauth' &&
        (!EMAIL_PROVIDERS.find(item => item.id === provider)?.host ||
          provider === 'other')
    );
  });
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message?: string;
  } | null>(null);

  const {
    register,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = form;

  const utilisateur = useAuthStore(state => state.user);
  const nomConnecte = [utilisateur?.firstName, utilisateur?.lastName]
    .filter(Boolean)
    .join(' ');

  const selectedProvider = watch('smtpProvider');
  const smtpHost = watch('smtpHost');
  const smtpPort = watch('smtpPort');
  const smtpSsl = watch('smtpSsl');
  const smtpUser = watch('smtpUser');
  const smtpPassword = watch('smtpPassword');

  const isMicrosoftOAuthProvider = selectedProvider === 'microsoft365-oauth';
  const isGmailProvider = selectedProvider === 'gmail-oauth';
  const isOAuthProvider = isMicrosoftOAuthProvider || isGmailProvider;
  const isSansBoite = selectedProvider === SANS_BOITE_PROVIDER_ID;
  const hasSelectedProvider = Boolean(selectedProvider);
  const isManualProvider =
    hasSelectedProvider && !isOAuthProvider && !isSansBoite;
  const selectedProviderInfo = EMAIL_PROVIDERS.find(
    item => item.id === selectedProvider
  );
  const manualProviders = EMAIL_PROVIDERS.filter(provider =>
    MANUAL_PROVIDER_IDS.includes(provider.id)
  );

  const providerCards = [
    {
      id: 'microsoft365-oauth',
      title: 'Microsoft 365 / Outlook',
      description: 'Connexion sécurisée, sans mot de passe stocké.',
      logo: '/providers/microsoft365.svg',
      color: 'blue',
    },
    {
      id: 'gmail-oauth',
      title: 'Gmail',
      description: 'Connexion sécurisée avec votre compte Google.',
      logo: '/providers/gmail.png',
      color: 'red',
    },
    {
      id: 'other',
      title: 'Autre boîte mail',
      description: 'OVH, IONOS, Hostinger, Coaxis, LWS ou autre.',
      logo: undefined,
      color: 'neutral',
    },
    {
      id: SANS_BOITE_PROVIDER_ID,
      title: 'Envoyer à mon nom',
      description:
        'Sans boîte mail à connecter : vos clients voient votre nom et vous répondent directement.',
      logo: undefined,
      color: 'neutral',
    },
  ] as const;

  const applyProviderSettings = (provider: EmailProvider) => {
    setValue('smtpProvider', provider.id, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('smtpHost', provider.host, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue('smtpPort', provider.port, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue('smtpSsl', provider.useSsl, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue('smtpTls', provider.useTls, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setTestResult(null);
    clearErrors(['smtpHost', 'smtpPort', 'smtpUser', 'smtpPassword']);
    setShowAdvancedSettings(!provider.host || provider.id === 'other');
  };

  const handleSelectProvider = (providerId: string) => {
    if (providerId === SANS_BOITE_PROVIDER_ID) {
      setValue('smtpProvider', SANS_BOITE_PROVIDER_ID, {
        shouldDirty: true,
        shouldValidate: true,
      });
      // L'adresse de réponse doit être celle d'un membre du cabinet : on
      // propose celle de la personne connectée (un expéditeur par
      // collaboratrice, à son nom). Seulement ici : pour une boîte SMTP, le
      // « De » doit rester celui de la boîte (paie@…), jamais une adresse
      // personnelle (relevé QA R1 du 20/09/2026).
      if (!getValues('smtpFromEmail') && utilisateur?.email) {
        setValue('smtpFromEmail', utilisateur.email, { shouldDirty: true });
      }
      if (!getValues('smtpFromName') && nomConnecte) {
        setValue('smtpFromName', nomConnecte, { shouldDirty: true });
      }
      setTestResult(null);
      clearErrors(['smtpHost', 'smtpPort', 'smtpUser', 'smtpPassword']);
      setShowAdvancedSettings(false);
      return;
    }

    if (providerId === 'other') {
      const fallbackProvider = EMAIL_PROVIDERS.find(
        item => item.id === 'other'
      );
      if (fallbackProvider) {
        applyProviderSettings(fallbackProvider);
      }
      return;
    }

    const provider = EMAIL_PROVIDERS.find(item => item.id === providerId);
    if (!provider) return;

    applyProviderSettings(provider);
    setShowAdvancedSettings(false);
  };

  const handleConnectOAuth = async () => {
    setIsOAuthLoading(true);
    try {
      const { url } = isGmailProvider
        ? await featureSendersService.getGoogleOAuthAuthorizeUrl()
        : await featureSendersService.getOAuthAuthorizeUrl();
      window.location.href = url;
    } catch {
      toast.error(
        t('senders.form.oauthError', "Impossible d'initier la connexion")
      );
      setIsOAuthLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!smtpHost || !smtpPort || !smtpUser || !smtpPassword) {
      toast.error(
        t(
          'senders.form.testError',
          'Remplissez au minimum l’adresse email, le mot de passe et le serveur.'
        )
      );
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await sendersService.testSmtpConnection({
        host: smtpHost,
        port: smtpPort,
        secure: smtpSsl || false,
        user: smtpUser,
        password: smtpPassword,
      });

      if (result.success) {
        setTestResult({
          success: true,
          message: t('senders.form.testSuccess', 'Connexion réussie'),
        });
        toast.success(t('senders.form.testSuccess', 'Connexion réussie'));
      } else {
        const message = getHumanSmtpError(result.error);
        setTestResult({
          success: false,
          message,
        });
        toast.error(message);
      }
    } catch (error) {
      const rawMessage =
        error instanceof Error ? error.message : t('common.genericError');
      const message = getHumanSmtpError(rawMessage);
      setTestResult({
        success: false,
        message,
      });
      toast.error(message);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <section>
        <div className="mb-4">
          <h4 className="text-base font-semibold text-neutral-950">
            {t('senders.form.providerTitle', 'Comment envoyer vos emails ?')}
          </h4>
          <p className="mt-1 text-sm text-neutral-500">
            {t(
              'senders.form.providerHelp',
              'Aucun réglage n’est appliqué avant votre choix.'
            )}
          </p>
          {errors.smtpProvider && (
            <p className="text-error-600 mt-2 text-sm">
              {errors.smtpProvider.message as string}
            </p>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {providerCards.map(card => {
            const isSelected =
              card.id === 'other'
                ? isManualProvider
                : selectedProvider === card.id;
            const selectedClass =
              card.color === 'blue'
                ? 'border-blue-500 ring-2 ring-blue-100'
                : card.color === 'red'
                  ? 'border-red-400 ring-2 ring-red-100'
                  : 'border-neutral-900 ring-2 ring-neutral-100';

            return (
              <button
                key={card.id}
                type="button"
                onClick={() => handleSelectProvider(card.id)}
                className={cn(
                  'focus-visible:ring-primary-500 flex min-h-[140px] flex-col justify-between rounded-lg border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                  isSelected ? selectedClass : 'border-neutral-200'
                )}
              >
                <span className="flex items-center justify-between gap-3">
                  <span
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-lg',
                      card.color === 'neutral'
                        ? 'bg-neutral-900 text-white'
                        : 'border border-neutral-200 bg-white'
                    )}
                  >
                    {card.logo ? (
                      <img
                        src={card.logo}
                        alt=""
                        aria-hidden="true"
                        className="h-6 w-6 object-contain"
                      />
                    ) : card.id === SANS_BOITE_PROVIDER_ID ? (
                      <Send className="h-5 w-5" />
                    ) : (
                      <Mail className="h-5 w-5" />
                    )}
                  </span>
                  {isSelected && (
                    <CheckCircle className="h-5 w-5 text-neutral-900" />
                  )}
                </span>
                <span>
                  <span className="block text-base font-semibold text-neutral-950">
                    {card.title}
                  </span>
                  <span className="mt-1 block text-sm leading-5 text-neutral-600">
                    {card.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {!hasSelectedProvider ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-5 py-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-neutral-500">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-950">
                Choisissez d’abord comment envoyer.
              </p>
              <p className="mt-1 text-sm leading-6 text-neutral-600">
                Les champs adaptés apparaîtront ensuite.
              </p>
            </div>
          </div>
        </div>
      ) : isOAuthProvider ? (
        <div className="rounded-lg border border-neutral-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg border border-neutral-200 bg-white">
            <img
              src={
                isGmailProvider
                  ? '/providers/gmail.png'
                  : '/providers/microsoft365.svg'
              }
              alt=""
              aria-hidden="true"
              className="h-7 w-7 object-contain"
            />
          </div>
          <h4 className="mb-2 text-lg font-semibold text-neutral-950">
            {t(
              'senders.form.oauthTitle',
              isGmailProvider ? 'Connexion Gmail' : 'Connexion Microsoft 365'
            )}
          </h4>
          <p className="mx-auto mb-4 max-w-xl text-sm leading-6 text-neutral-600">
            {t(
              'senders.form.oauthDescription',
              isGmailProvider
                ? "Vous serez redirigée vers Google pour autoriser Innov'Paie à envoyer des emails depuis cette boîte Gmail."
                : "Vous serez redirigée vers Microsoft pour autoriser Innov'Paie à envoyer des emails depuis cette boîte."
            )}
          </p>
          <Button
            type="button"
            onClick={handleConnectOAuth}
            disabled={isOAuthLoading}
            className={cn(
              isGmailProvider
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-[#0078D4] hover:bg-[#106EBE]'
            )}
          >
            {isOAuthLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('senders.form.oauthConnecting', 'Redirection...')}
              </>
            ) : (
              <>
                <ExternalLink className="mr-2 h-4 w-4" />
                {t(
                  'senders.form.oauthConnect',
                  isGmailProvider
                    ? 'Se connecter avec Google'
                    : 'Se connecter avec Microsoft'
                )}
              </>
            )}
          </Button>
        </div>
      ) : isSansBoite ? (
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-neutral-200 bg-white p-5 shadow-sm md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-900">
              Nom affiché <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('smtpFromName')}
              placeholder="Nom vu par vos clients"
              className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
            />
            {errors.smtpFromName && (
              <p className="text-error-600 mt-1 text-sm">
                {errors.smtpFromName.message as string}
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-900">
              Adresse de réponse <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              {...register('smtpFromEmail')}
              placeholder="Adresse des réponses"
              className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
            />
            {errors.smtpFromEmail && (
              <p className="text-error-600 mt-1 text-sm">
                {errors.smtpFromEmail.message as string}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-5 rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 border-b border-neutral-100 pb-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h4 className="text-base font-semibold text-neutral-950">
                Connexion SMTP classique
              </h4>
              <p className="mt-1 text-sm text-neutral-600">
                Choisissez le fournisseur, puis renseignez la boîte mail.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
              <ShieldCheck className="h-4 w-4" />
              Relais sécurisé disponible
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-900">
              Fournisseur
            </label>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {manualProviders.map(provider => {
                const isSelected = selectedProvider === provider.id;
                return (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => applyProviderSettings(provider)}
                    className={cn(
                      'focus-visible:ring-primary-500 flex h-14 items-center justify-center gap-2 rounded-lg border bg-white px-3 text-sm font-semibold text-neutral-800 transition hover:border-neutral-300 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                      isSelected
                        ? 'border-neutral-900 ring-2 ring-neutral-100'
                        : 'border-neutral-200'
                    )}
                  >
                    <span className="flex flex-col items-center gap-1">
                      {provider.logo ? (
                        <img
                          src={provider.logo}
                          alt=""
                          aria-hidden="true"
                          className="max-h-6 max-w-[86px] object-contain"
                        />
                      ) : (
                        <Mail className="h-4 w-4" />
                      )}
                      <span className="text-[11px] leading-none">
                        {getProviderLabel(provider)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {selectedProviderInfo?.note && (
              <p className="mt-2 text-xs leading-5 text-neutral-500">
                {selectedProviderInfo.note}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('senders.form.smtpUser', 'Adresse email')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                {...register('smtpUser')}
                placeholder="Adresse email"
                autoComplete="username"
                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
              />
              {errors.smtpUser && (
                <p className="text-error-600 mt-1 text-sm">
                  {errors.smtpUser.message as string}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('senders.form.smtpPassword', 'Mot de passe')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('smtpPassword')}
                  placeholder="Mot de passe"
                  autoComplete="current-password"
                  className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 pr-12 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(value => !value)}
                  className="focus-visible:ring-primary-500 absolute top-1/2 right-2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 focus-visible:ring-2 focus-visible:outline-none"
                  aria-label={
                    showPassword
                      ? 'Masquer le mot de passe'
                      : 'Afficher le mot de passe'
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.smtpPassword && (
                <p className="text-error-600 mt-1 text-sm">
                  {errors.smtpPassword.message as string}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('senders.form.smtpFromName', 'Nom affiché')}
              </label>
              <input
                type="text"
                {...register('smtpFromName')}
                placeholder="Nom affiché"
                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
              />
              <p className="mt-1 text-xs text-neutral-500">Optionnel</p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                {t('senders.form.smtpFromEmail', 'Adresse affichée')}
              </label>
              <input
                type="email"
                {...register('smtpFromEmail')}
                placeholder="Même adresse si laissé vide"
                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
              />
              <p className="mt-1 text-xs text-neutral-500">Optionnel</p>
            </div>
          </div>

          <div className="rounded-lg border border-neutral-200 bg-white">
            <button
              type="button"
              onClick={() => setShowAdvancedSettings(value => !value)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-neutral-900">
                <Settings2 className="h-4 w-4 text-neutral-500" />
                Paramètres serveur
              </span>
              <span className="flex items-center gap-2 text-xs text-neutral-500">
                {selectedProviderInfo?.host
                  ? `${selectedProviderInfo.host}:${selectedProviderInfo.port}`
                  : 'À compléter'}
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    showAdvancedSettings && 'rotate-180'
                  )}
                />
              </span>
            </button>

            <div
              className={cn(
                'border-t border-neutral-200 p-4',
                !showAdvancedSettings && 'hidden'
              )}
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-900">
                    {t('senders.form.smtpHost', 'Serveur SMTP')}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('smtpHost')}
                    placeholder="Serveur SMTP"
                    className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
                  />
                  {errors.smtpHost && (
                    <p className="text-error-600 mt-1 text-sm">
                      {errors.smtpHost.message as string}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-900">
                    {t('senders.form.smtpPort', 'Port')}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    {...register('smtpPort', { valueAsNumber: true })}
                    placeholder="587"
                    className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 transition-colors placeholder:text-neutral-400/50 focus:ring-2"
                  />
                  {errors.smtpPort && (
                    <p className="text-error-600 mt-1 text-sm">
                      {errors.smtpPort.message as string}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-3 text-sm text-neutral-700">
                  <input type="checkbox" {...register('smtpSsl')} />
                  Utiliser SSL
                </label>

                <label className="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-3 text-sm text-neutral-700">
                  <input type="checkbox" {...register('smtpTls')} />
                  Utiliser TLS / STARTTLS
                </label>
              </div>
            </div>
          </div>

          {testResult && (
            <div
              className={cn(
                'flex items-center gap-3 rounded-lg p-4',
                testResult.success
                  ? 'border border-green-200 bg-green-50'
                  : 'border border-red-200 bg-red-50'
              )}
            >
              {testResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <p
                className={cn(
                  'text-sm',
                  testResult.success ? 'text-green-900' : 'text-red-900'
                )}
              >
                {testResult.message}
              </p>
            </div>
          )}

          {showTestButton && (
            <div className="flex justify-start">
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={isTesting}
              >
                {isTesting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('senders.form.testing', 'Test en cours...')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {t('senders.form.testConnection', 'Tester la connexion')}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
